import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Control, Controller, useFieldArray, useForm, UseFormGetValues } from "react-hook-form";
import { debounce } from "lodash-es";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Card, FormGroup, PageHeader, Input, Textarea, Button } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";

import { CircularProgress, Grid2 as Grid, Typography, Tabs, Tab, Alert } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import ApplicationLanguageSelect from "components/selects/ApplicationLanguageSelect";

import { Language } from "imports/Utils";

import TemplateAddLanguageDialog from "view/templates/components/dialogs/TemplateAddLanguageDialog";
import TemplateVersionSelect from "view/templates/components/selects/TemplateVersionSelect";

import { DateUtils } from "utils/DateUtils";

import { GetDefaultTemplateByIdDocument, GetDefaultTemplateByNameDocument } from "__generated__/view/templates/graphql/template.queries.generated";
import { SaveTemplateSetDocument } from "__generated__/view/templates/graphql/template.mutations.generated";
import { TemplateSetFragment, TemplateFragment } from "__generated__/view/templates/graphql/template.fragments.generated";

import { templateSchema, TemplateSchemaInput, TemplateSchemaOutput } from "./utils/template.schema";

const TemplateEdit = () => {

	const navigate = useNavigate();

	const { idOrName } = useParams();

	const isId = /^\d+$/.test(idOrName ?? "");
	const id = isId ? Number(idOrName) : null;
	const name = !isId ? idOrName : null;

	const { data: templateSetById } = useQuery(GetDefaultTemplateByIdDocument, {
		skip: !id,
		variables: {
			id: id!,
		},
	});

	const { data: templateSetByName } = useQuery(GetDefaultTemplateByNameDocument, {
		skip: !!id,
		variables: {
			name: name ?? "",
		},
	});

	const templateSet = templateSetById?.getById || templateSetByName?.getByName;

	return templateSet ? (
		<TemplateForm templateSet={templateSet} navigate={navigate} />
	) : (
		<CircularProgress size={30} />
	);
};

const TemplateForm = ({ templateSet, navigate }: { templateSet: TemplateSetFragment, navigate: ReturnType<typeof useNavigate> }) => {

	const { control, handleSubmit, getValues } = useForm<TemplateSchemaInput, unknown, TemplateSchemaOutput>({
		resolver: zodResolver(templateSchema),
		defaultValues: {
			id: templateSet.id ?? null,
			name: templateSet.name,
			version: templateSet.version ?? null,
			preferredLanguage: templateSet.preferredLanguage ?? null,
			latestTemplates: templateSet.latestTemplates.map(template => ({
				...template,
				id: template.id ?? null,
				version: intl.formatMessage({ id: "CURRENT" }),
				subject: template.subject ?? "",
			})),
		},
	});

	const [ saveTemplateSet, { loading } ] = useMutation(SaveTemplateSetDocument);

	const save = async (data: TemplateSchemaOutput, saveAsFinal: boolean) => {

		const preferredLanguageChanged = data.preferredLanguage?.code !== templateSet.preferredLanguage?.code;

		const changedTemplates = data.latestTemplates
			.filter(template => {
				const existingTemplate = templateSet.latestTemplates
					.find(t => t.language.code === template.language.code);

				return existingTemplate == undefined || (
					saveAsFinal && existingTemplate.isFinal !== saveAsFinal || // Allow unchanged drafts to be saved as final
					existingTemplate.subject !== template.subject ||
					existingTemplate.template !== template.template
				);
			}).map(template => ({
				name: template.name,
				language: template.language.code,
				subject: template.subject,
				template: template.template,
			}));

		const removedTemplates = templateSet.latestTemplates
			.filter(template => data.latestTemplates.every(t => t.language.code !== template.language.code))
			.map(template => ({
				name: template.name,
				language: template.language.code,
				subject: template.subject,
				template: template.template,
				removed: true,
			}));

		if (preferredLanguageChanged || changedTemplates.length > 0 || removedTemplates.length > 0) {
			await saveTemplateSet({
				variables: {
					input: {
						id: templateSet.id,
						name: templateSet.name,
						preferredLanguage: data.preferredLanguage?.code ?? null,
						changedTemplates: [ ...changedTemplates, ...removedTemplates ],
					},
					saveAsFinal: saveAsFinal,
				},
			});
		}

		navigate("/view/templates");
	};

	return (
		<form>
			<PageHeader>
				<PageHeader.Title>
					{Language("TEMPLATE")}
					<Helmet title={intl.formatMessage({ id: "TEMPLATE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/templates")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button
						loading={loading}
						type="submit"
						variant="outlined"
						onClick={handleSubmit(data => save(data, false))}
					>
						{Language("SAVE_AS_DRAFT")}
					</Button>

					<Button loading={loading} onClick={handleSubmit(data => save(data, true))}>
						{Language("SAVE_AS_FINAL")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<TemplateSetDetails
				control={control}
				templateSet={templateSet}
				getValues={getValues}
			/>
		</form>
	);
};

const TemplateSetDetails = ({ templateSet, control, getValues }: {
	templateSet: TemplateSetFragment,
	control: Control<TemplateSchemaInput>,
	getValues: UseFormGetValues<TemplateSchemaInput>,
}) => {

	const [ selectedIndex, setSelectedIndexRaw ] = useState(0);

	const setSelectedIndex = (index: number) => {
		setSelectedIndexRaw(index);
		loadPreview(fields[index].template, fields[index].language.code);
	};

	const [ dialogOpen, setDialogOpen ] = useState(false);

	const { fields, append, remove, update } = useFieldArray({
		control,
		name: "latestTemplates",
	});

	const existingLanguageCodes = fields
		.map(template => template.language.code)
		.filter(code => typeof code === "string");

	const [ currentVersionTemplates, setCurrentVersionTemplates ] = useState<Record<string, TemplateFragment>>(() =>
		fields.reduce((acc, cur) => {
			if (cur.language.code) {
				acc[cur.language.code] = cur;
			}
			return acc;
		}, {} as Record<string, TemplateFragment>),
	);

	const [ preview, setPreview ] = useState<string | null | undefined>();
	const { instance } = useCoastguard();

	const loadPreview = (template: string, languageCode: string) => {
		setPreview(undefined);

		if (template) {
			instance.fetchBlob("/templates/preview", {
				method: "POST",
				body: {
					template: template,
					language: languageCode,
				},
				headers: {
					"Accept": "application/pdf",
					"Content-Type": "application/json",
				},
			}).then(response => {
				setPreview(URL.createObjectURL(response));
			}).catch(error => {
				console.error("Unable to parse template preview:", error);
				setPreview(null);
			});
		} else {
			setPreview(null);
		}

	};

	const loadPreviewRef = useRef(loadPreview);

	useEffect(() => {
		loadPreviewRef.current = loadPreview;
	}, [ loadPreview ]);

	const debouncedLoadPreview = useMemo(() => debounce(loadPreviewRef.current, 500), []);

	useEffect(() => {
		const currentTemplate = getValues(`latestTemplates.${selectedIndex}`);

		loadPreview(currentTemplate.template, currentTemplate.language.code);
	}, []);

	return (
		<Grid spacing={3} container>
			<Grid size={{ xs: 12, md: 6 }}>
				<Card>
					<Grid spacing={3} container>
						<Grid size={{ xs: 12, md: 6, lg: 5 }}>
							<FormGroup label={Language("TEMPLATES_NAME")} overflow="hidden">
								<Typography>{getValues("name")}</Typography>
							</FormGroup>

							<FormGroup label={Language("TEMPLATES_LANGUAGE")} overflow="hidden">
								<Controller
									name="preferredLanguage"
									control={control}
									render={({ field }) => (
										<ApplicationLanguageSelect
											{...field}
											autoFocus={!!templateSet.id}
											getOptionDisabled={option => !existingLanguageCodes.includes(option.code)}
										/>
									)}
								/>
							</FormGroup>
						</Grid>

						<Grid size={12} container spacing={1}>
							<Grid size={12} sx={{ borderBottom: 1, marginBottom: 1, borderColor: "divider" }}>
								<Tabs value={selectedIndex} onChange={(_, newValue) => setSelectedIndex(newValue)}>
									{fields.map((template, index) => (
										<Tab label={template.language.code} value={index} key={template.id} />
									))}
								</Tabs>
							</Grid>

							<Grid size={12} sx={{ display: "flex", gap: 1 }}>
								<Button onClick={() => setDialogOpen(true)} size="small" variant="outlined">
									{Language("TEMPLATES_ADD_LANGUAGE")}
								</Button>

								<Button
									onClick={() => {
										remove(selectedIndex);
										setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : 0);
									}}
									size="small"
									color="danger"
									variant="outlined"
								>
									{Language("TEMPLATES_REMOVE_THIS_LANGUAGE")}
								</Button>
							</Grid>

							<Grid
								size={12}
								spacing={3}
								display="flex"
								flexDirection="row-reverse"
								justifyContent="space-between"
								container
							>
								<Grid size={{ xs: 12, sm: 6, md: 5 }}>
									<FormGroup label={Language("TEMPLATES_VERSION")}>
										<Controller
											name={`latestTemplates.${selectedIndex}.version`}
											control={control}
											render={({ field }) => (
												<TemplateVersionSelect
													{...field}
													value={{
														...fields[selectedIndex],
														version: fields[selectedIndex].version,
													}}
													search={{
														templateName: fields[selectedIndex].name,
														languageCode: fields[selectedIndex].language.code,
													}}
													disabled={fields[selectedIndex].version === intl.formatMessage({ id: "NEW" })}
													onChange={value => {
														if (value) {
															field.onChange(value.version);

															const newTemplate = value.version === intl.formatMessage({ id: "CURRENT" }) && fields[selectedIndex].language.code
																? currentVersionTemplates[fields[selectedIndex].language.code]
																: value;

															update(selectedIndex, {
																...newTemplate,
																id: newTemplate.id ?? null,
																version: newTemplate.version ?? null,
																subject: newTemplate.subject ?? "",
															});
															loadPreview(newTemplate.template, newTemplate.language.code);
														}
													}}
												/>
											)}
										/>
									</FormGroup>
								</Grid>

								<TemplateDetails
									key={fields[selectedIndex].id}
									control={control}
									templateSet={templateSet}
									selectedIndex={selectedIndex}
									readonly={fields[selectedIndex].version !== intl.formatMessage({ id: "CURRENT" }) && fields[selectedIndex].version !== intl.formatMessage({ id: "NEW" })}
									setCurrentVersionTemplate={getCurrentTemplate => {
										const currentLanguageCode = fields[selectedIndex].language.code;

										setCurrentVersionTemplates(curTemplates => {
											const currentTemplate = getCurrentTemplate(curTemplates[currentLanguageCode]);
											debouncedLoadPreview(currentTemplate.template, currentLanguageCode);
											return ({
												...curTemplates,
												[currentLanguageCode]: currentTemplate,
											});
										});
									}}
								/>
							</Grid>

						</Grid>
					</Grid>
				</Card>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }} display="flex" justifyContent="center">
				<TemplatePreview preview={preview} />
			</Grid>

			<TemplateAddLanguageDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onAdd={language => append({
					id: null,
					name: templateSet.name,
					language: language,
					template: "",
					subject: "",
					version: intl.formatMessage({ id: "NEW" }),
					isFinal: false,
					created: DateUtils.getTimestamp(new Date())!,
				})}
				existingLanguages={fields.map(template => template.language)}
			/>
		</Grid>
	);
};

const TemplateDetails = ({ selectedIndex, templateSet, readonly, control, setCurrentVersionTemplate }: {
	selectedIndex: number,
	templateSet: TemplateSetFragment,
	readonly: boolean,
	control: Control<TemplateSchemaInput>,
	setCurrentVersionTemplate: (getNewTemplate: (currentTemplate: TemplateFragment) => TemplateFragment) => void,
}) => (
	<>
		<Grid size={{ xs: 12, sm: 6, md: 5 }}>
			<FormGroup label={Language("TEMPLATES_SUBJECT")} overflow="hidden">
				<Controller
					name={`latestTemplates.${selectedIndex}.subject`}
					control={control}
					render={({ field }) => (
						<Input
							{...field}
							value={field.value}
							autoFocus={!templateSet.id}
							disabled={readonly}
							placeholder={intl.formatMessage({ id: "TEMPLATES_SUBJECT" })}
							onChange={event => {
								field.onChange(event.target.value);

								setCurrentVersionTemplate(currentTemplate => ({
									...currentTemplate,
									subject: event.target.value,
								}));
							}}
						/>
					)}
				/>
			</FormGroup>
		</Grid>

		<Grid size={12}>
			<FormGroup label={Language("TEMPLATE")} overflow="hidden">
				<Controller
					name={`latestTemplates.${selectedIndex}.template`}
					control={control}
					render={({ field }) => (
						<Textarea
							{...field}
							disabled={readonly}
							rows={15}
							onChange={event => {
								field.onChange(event.target.value);

								setCurrentVersionTemplate(currentTemplate => ({
									...currentTemplate,
									template: event.target.value,
								}));
							}}
						/>
					)}
				/>
			</FormGroup>
		</Grid>
	</>
);

const TemplatePreview = ({ preview }: {
	preview: string | null | undefined,
}) => preview === null ? (
	<Alert severity="warning" sx={{ flexGrow: 1, alignSelf: "flex-start" }}>
		{intl.formatMessage({ id: "TEMPLATE_CANNOT_LOAD_PREVIEW_INVALID_TEMPLATE" })}
	</Alert>
) : preview === undefined ? (
	<CircularProgress size={24} />
) : (
	<iframe
		src={preview}
		title={intl.formatMessage({ id: "TEMPLATE_PREVIEW" })}
		style={{ alignSelf: "stretch", flexGrow: 1, minHeight: 500 }}
	/>
);

export default TemplateEdit;
